import { alpha, styled, useTheme } from '@mui/material/styles';
import { Button } from 'common/components/material';
import { useDrawer, useView } from 'common/hooks';
import { FeatureGuard } from 'features/feature-access/components/feature-guard.component';
import { FeaturesEnum } from 'features/feature-access/features.enum';
import { FeatureAccessEnum } from 'features/feature-access/enums/feature-access.enum';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { DrawerIdEnum } from '../enums/drawer-id.enum';
import { useState } from 'react';

const StyledButtonContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const AiIcon = styled(AutoAwesomeIcon)(({ theme }) => ({
  fontSize: 17,
  color: theme.palette.Buttons.Secondary.SecondaryLabel,
  '& path': {
    fill: 'url(#ask-connectx-ai-gradient)',
  },
  '@media (forced-colors: active)': {
    '& path': {
      fill: 'currentColor',
    },
  },
}));

const AskConnectXButton = () => {
  const { isDesktopView } = useView();
  const theme = useTheme();

  const { openDrawer, closeDrawer } = useDrawer();

  const [isChatSideDrawerOpen, setIsChatSideDrawerOpen] = useState(false);

  useDrawer((drawerState: any) => {
    if (drawerState.drawerId === DrawerIdEnum.ASK_CONNECTX_DRAWER) {
      setIsChatSideDrawerOpen(drawerState.open);
    }
  });

  const toggleAskConnectXDrawer = () => {
    if (isChatSideDrawerOpen) {
      closeDrawer(DrawerIdEnum.ASK_CONNECTX_DRAWER);
    } else {
      openDrawer(DrawerIdEnum.ASK_CONNECTX_DRAWER, {});
    }
  };

  if (!isDesktopView) return null;

  return (
    <FeatureGuard
      features={[
        {
          feature: FeaturesEnum.MATCHING_ENGINE,
          access: FeatureAccessEnum.WRITE,
        },
      ]}
    >
      <StyledButtonContainer>
        <svg
          width="0"
          height="0"
          aria-hidden="true"
          focusable="false"
          style={{ position: 'absolute' }}
        >
          <defs>
            <linearGradient id="ask-connectx-ai-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={theme.palette.primary.light} />
              <stop offset="48%" stopColor={theme.palette.Buttons.Secondary.SecondaryLabel} />
              <stop offset="100%" stopColor={theme.palette.secondary.main} />
            </linearGradient>
          </defs>
        </svg>

        <Button
          id="global-ask-connectx-button"
          onClick={toggleAskConnectXDrawer}
          aria-expanded={isChatSideDrawerOpen}
          aria-controls="ask-connectx-drawer"
          btntype="secondary"
          size="small"
          startIcon={<AiIcon />}
          sx={{
            position: 'relative',
            whiteSpace: 'nowrap',
            minHeight: 32,
            fontWeight: 600,

            transition:
              'background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease',

            backgroundColor: isChatSideDrawerOpen
              ? theme.palette.Buttons.Secondary.BgPressed
              : theme.palette.Buttons.Secondary.BgDefault,

            borderColor: isChatSideDrawerOpen
              ? theme.palette.Buttons.Secondary.SecondaryLabel
              : 'transparent',

            color: theme.palette.Buttons.Secondary.SecondaryLabel,

            '&:hover': {
              backgroundColor: theme.palette.Buttons.Secondary.BgHover,
              borderColor: isChatSideDrawerOpen
                ? theme.palette.Buttons.Secondary.SecondaryLabel
                : 'transparent',
            },

            '&.Mui-focusVisible': {
              boxShadow: `0 0 0 3px ${alpha(
                theme.palette.Buttons.Secondary.SecondaryLabel,
                0.24,
              )}`,
            },
          }}
        >
          Ask ConnectX
        </Button>
      </StyledButtonContainer>
    </FeatureGuard>
  );
};

export default AskConnectXButton;
